import { JSX, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getDashboardWidgetDataApi } from "src/api/dashboardApi";

type DataRow = Record<string, unknown>;

type LimitOption = { value: number | string; label: string };

type MetaFilter = {
  key: string;
  label?: string;
  default?: number | string;
  options?: LimitOption[];
};

type RuleCondition = {
  gt?: number;
  gte?: number;
  lt?: number;
  lte?: number;
  in?: (string | number)[];
};

type ColorRule = {
  is_badge: boolean;
  red_hex_code: string;
  red_condition: RuleCondition;
  yellow_hex_code: string;
  yellow_condition: RuleCondition;
  green_hex_code: string;
  green_condition: RuleCondition;
};

type RuleMatch = { color: string; bg: string } | null;

type Metadata = {
  fence_stage?: string;
  n_decided_cases?: number;
  n_outliers?: number;
  fence?: number;
  median?: number;
  mean?: number;
  q1?: number;
  q3?: number;
  p90?: number;
  iqr?: number;
  limit?: number;
  filters?: MetaFilter[];
  rules?: Record<string, ColorRule>;
  [key: string]: unknown;
};

type CustomTableData = {
  data?: DataRow[];
  metadata?: Metadata;
};

type CustomTableWidgetProps = {
  isLoading: boolean;
  data: unknown;
  dashboardName?: string;
  widgetTemplateId?: string | number;
  appliedFilters?: Record<string, unknown>;
};

const FALLBACK_LIMIT_OPTIONS: LimitOption[] = [
  { value: 10, label: "Top 10" },
  { value: 20, label: "Top 20" },
  { value: 50, label: "Top 50" },
  { value: 100, label: "Top 100" },
  { value: "all", label: "All" },
];

const FALLBACK_LIMIT_DEFAULT = 10;

const isNumeric = (val: unknown): val is number =>
  typeof val === "number" && !isNaN(val);

const unwrap = (raw: unknown): CustomTableData => {
  if (!raw || typeof raw !== "object") return {};
  const obj = raw as Record<string, unknown>;
  if (Array.isArray(obj.data)) return obj as CustomTableData;
  if (Array.isArray(obj)) return { data: obj as DataRow[] };
  return obj as CustomTableData;
};

function matchesCondition(val: unknown, cond: RuleCondition): boolean {
  if (cond.in != null) {
    const s = String(val ?? "").toLowerCase();
    return cond.in.some((v) => String(v).toLowerCase() === s);
  }
  if (!isNumeric(val)) return false;
  if (cond.gt != null && !(val > cond.gt)) return false;
  if (cond.gte != null && !(val >= cond.gte)) return false;
  if (cond.lt != null && !(val < cond.lt)) return false;
  if (cond.lte != null && !(val <= cond.lte)) return false;
  return true;
}

function evaluateRule(val: unknown, rule: ColorRule): RuleMatch {
  if (matchesCondition(val, rule.red_condition)) {
    return { color: rule.red_hex_code, bg: hexToBg(rule.red_hex_code) };
  }
  if (matchesCondition(val, rule.yellow_condition)) {
    return { color: rule.yellow_hex_code, bg: hexToBg(rule.yellow_hex_code) };
  }
  if (matchesCondition(val, rule.green_condition)) {
    return { color: rule.green_hex_code, bg: hexToBg(rule.green_hex_code) };
  }
  return null;
}

function buildSummary(
  meta: Metadata | undefined,
  limitValue: number | string,
): string | null {
  if (!meta) return null;
  const totalKey = Object.keys(meta).find((k) => k.startsWith("total_") && isNumeric(meta[k]));
  if (!totalKey) return null;
  const total = meta[totalKey] as number;
  const entity = totalKey.replace(/^total_/, "").replace(/_/g, " ");
  const showing =
    limitValue === "all" ? "all" : `top ${limitValue}`;
  return `Showing ${showing} of ${total.toLocaleString()} ${entity}`;
}

function hexToBg(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, 0.08)`;
}

export default function CustomTableWidget({
  isLoading,
  data,
  dashboardName,
  widgetTemplateId,
  appliedFilters,
}: CustomTableWidgetProps): JSX.Element {
  const [limit, setLimit] = useState<number | string | null>(null);

  const canFetch =
    !!dashboardName &&
    !!widgetTemplateId &&
    !!appliedFilters &&
    Object.keys(appliedFilters).length > 0;

  const { data: queryData, isLoading: isFetching } = useQuery({
    queryKey: ["widget", dashboardName, widgetTemplateId, appliedFilters, limit],
    queryFn: () =>
      getDashboardWidgetDataApi(dashboardName!, String(widgetTemplateId!), {
        filters: appliedFilters,
        widget_filters: limit === null ? undefined : { top_n: limit },
      }),
    enabled: canFetch,
    staleTime: 0,
    gcTime: 0,
  });

  const parsed = useMemo(
    () => unwrap(canFetch ? queryData : data),
    [canFetch, queryData, data],
  );
  const rows = parsed.data ?? [];
  const meta = parsed.metadata;
  const rules = meta?.rules ?? {};

  const topNFilter = meta?.filters?.find((f) => f.key === "top_n");
  const limitOptions = topNFilter?.options?.length
    ? topNFilter.options
    : FALLBACK_LIMIT_OPTIONS;
  const limitValue = limit ?? topNFilter?.default ?? FALLBACK_LIMIT_DEFAULT;

  const columns = useMemo(() => {
    if (rows.length === 0) return [];
    return Object.keys(rows[0]);
  }, [rows]);

  const numericCols = useMemo(() => {
    const set = new Set<string>();
    for (const col of columns) {
      if (rows.some((r) => isNumeric(r[col]))) set.add(col);
    }
    return set;
  }, [columns, rows]);

  const rowBorderCol = useMemo(() => {
    for (const col of columns) {
      const rule = rules[col];
      if (rule?.is_badge) return col;
    }
    return null;
  }, [columns, rules]);

  if (canFetch ? isFetching : isLoading) {
    return (
      <div className="placeholder-glow h-100">
        <span className="placeholder w-100 h-100" />
      </div>
    );
  }

  const limitSelect = canFetch ? (
    <select
      className="form-select form-select-sm"
      style={{
        width: "auto",
        fontSize: 11,
        minWidth: 72,
        padding: "1px 24px 1px 6px",
        cursor: "pointer",
        marginLeft: "auto",
        flexShrink: 0,
      }}
      value={String(limitValue)}
      onChange={(e) => {
        const v = e.target.value;
        setLimit(v === "all" ? v : Number(v));
      }}
      aria-label="How many rows to show"
    >
      {limitOptions.map((o) => (
        <option key={String(o.value)} value={String(o.value)}>
          {o.label}
        </option>
      ))}
    </select>
  ) : null;

  if (rows.length === 0) {
    return (
      <div className="custom-table">
        {limitSelect && (
          <div className="custom-table__meta">{limitSelect}</div>
        )}
        <p className="text-muted small mb-0">No data available.</p>
      </div>
    );
  }

  const renderCell = (col: string, val: unknown) => {
    const rule = rules[col];

    if (rule) {
      const match = evaluateRule(val, rule);
      if (rule.is_badge) {
        const color = match?.color ?? "#64748b";
        const bg = match?.bg ?? "#f1f5f9";
        return (
          <span
            className="custom-table__badge"
            style={{ background: bg, color }}
          >
            <span
              className="custom-table__badge-dot"
              style={{ background: color }}
            />
            {val != null ? String(val) : "–"}
          </span>
        );
      }
      if (match) {
        const formatted = isNumeric(val)
          ? val % 1 === 0
            ? val.toLocaleString()
            : val.toFixed(1)
          : String(val ?? "–");
        const display = col.includes("%") ? `${formatted}%` : formatted;
        return <span style={{ color: match.color, fontWeight: 700 }}>{display}</span>;
      }
    }

    if (isNumeric(val) && numericCols.has(col)) {
      const formatted = val % 1 === 0 ? val.toLocaleString() : val.toFixed(1);
      const display = col.includes("%") ? `${formatted}%` : formatted;
      return <>{display}</>;
    }

    return <>{val != null ? String(val) : "–"}</>;
  };

  return (
    <div className="custom-table">
      {(meta || limitSelect) && (
        <div className="custom-table__meta">
          {buildSummary(meta, limitValue) && (
            <span
              style={{
                fontSize: 12,
                fontWeight: 500,
                color: "#64748b",
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              {buildSummary(meta, limitValue)}
            </span>
          )}
          {meta?.fence_stage && (
            <span className="custom-table__meta-stage">{meta.fence_stage}</span>
          )}
          <div className="custom-table__meta-stats">
            {meta?.n_outliers != null && (
              <span className="custom-table__meta-stat custom-table__meta-stat--red">
                {meta.n_outliers} outliers
              </span>
            )}
            {meta?.n_decided_cases != null && (
              <span className="custom-table__meta-stat">
                {meta.n_decided_cases} cases
              </span>
            )}
            {meta?.fence != null && (
              <span className="custom-table__meta-stat">
                Fence: {meta.fence.toFixed(1)}d
              </span>
            )}
            {meta?.median != null && (
              <span className="custom-table__meta-stat">
                Median: {meta.median.toFixed(1)}d
              </span>
            )}
          </div>
          {limitSelect}
        </div>
      )}

      <div className="custom-table__scroll">
        <table className="custom-table__table">
          <thead className="custom-table__head">
            <tr>
              {columns.map((col) => (
                <th key={col} className="custom-table__th">
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => {
              const borderMatch = rowBorderCol
                ? evaluateRule(row[rowBorderCol], rules[rowBorderCol])
                : null;
              return (
                <tr key={idx} className="custom-table__row">
                  {columns.map((col, ci) => (
                    <td
                      key={col}
                      className="custom-table__td"
                      style={
                        ci === 0 && borderMatch
                          ? { boxShadow: `inset 3px 0 0 ${borderMatch.color}` }
                          : undefined
                      }
                    >
                      {renderCell(col, row[col])}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
